import { getDict } from "../i18n";

/** €1,290 / 1 290 € — cents only when there are some. */
export function eur(value: number): string {
  const v = Math.round(value * 100) / 100;
  const digits = v % 1 ? 2 : 0;
  return new Intl.NumberFormat(getDict().format.locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
}

/** 29,000 / 29 000 */
export function num(value: number): string {
  return value.toLocaleString(getDict().format.locale);
}

/** Joins class names, skipping falsy values. */
export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Today in YYYY-MM-DD, for date input defaults. */
export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Adds n days to a YYYY-MM-DD string. */
export function addDays(date: string, n: number): string {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Copy of `obj` without the given keys. */
export function omit<T extends object, K extends keyof T>(
  obj: T,
  ...keys: K[]
): Omit<T, K> {
  const copy = { ...obj };
  for (const key of keys) delete copy[key];
  return copy;
}

/**
 * Date helpers read the "YYYY-MM-DD[THH:mm]" string directly instead of going
 * through Date, so a date never shifts by a day in another timezone.
 */
function dateParts(iso: string) {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return { y, month: getDict().format.months[m - 1], d };
}

/** "12 Oct" */
export function shortDate(iso: string): string {
  const { d, month } = dateParts(iso);
  return `${d} ${month}`;
}

/** "12 Oct 2026" */
export function longDate(iso: string): string {
  const { d, month, y } = dateParts(iso);
  return `${d} ${month} ${y}`;
}

/** "30 Sep at 09:12" */
export function shortDateTime(iso: string): string {
  return `${shortDate(iso)} ${getDict().format.at} ${iso.slice(11, 16)}`;
}

/** Adds n days to an ISO date, in UTC so the result never drifts a day. */
export function addDaysIso(iso: string, n: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Whole days from start to end (ISO dates). */
export function daysBetween(start: string, end: string): number {
  const ms = Date.parse(end.slice(0, 10)) - Date.parse(start.slice(0, 10));
  return Math.round(ms / 86_400_000);
}
